import React, { useState, useEffect, useCallback } from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const radarLegendDefs: Record<string, string> = {
  'Score': 'Relative strength score (0-100) in each training category based on your data.',
};

interface ScoreEntry {
  score: number | null;
  trend: 'up' | 'down' | 'flat' | null;
}

interface WeaknessData {
  scores: Record<string, ScoreEntry>;
  computed_at: string;
  has_data: boolean;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const AXES = [
  { key: 'sprint', label: 'Sprint' },
  { key: 'vo2max', label: 'VO2max' },
  { key: 'threshold', label: 'Threshold' },
  { key: 'cadence_efficiency', label: 'Cadence' },
  { key: 'fatigue_accumulation', label: 'Fatigue Mgmt' },
  { key: 'recovery_quality', label: 'Recovery' },
  { key: 'hr_drift', label: 'HR Drift' },
  { key: 'overtraining_risk', label: 'Overtrain Risk' },
  { key: 'aerobic_base', label: 'Aerobic Base' },
];

const TREND_ARROWS: Record<string, string> = {
  up: '\u2191',
  down: '\u2193',
  flat: '\u2013',
};

interface RadarDataPoint {
  axis: string;
  axisLabel: string;
  value: number;
  trend: string | null;
  insufficient: boolean;
}

const CustomTick: React.FC<any> = ({ x, y, payload, data }) => {
  const point = data?.find((d: RadarDataPoint) => d.axis === payload.value);
  const arrow = point?.trend ? TREND_ARROWS[point.trend] || '' : '';
  const label = point?.axisLabel || payload.value;
  const displayText = arrow ? `${label} ${arrow}` : label;

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={point?.insufficient ? '#ccc' : '#666'}
      fontSize={11}
      dominantBaseline="central"
    >
      {displayText}
    </text>
  );
};

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload as RadarDataPoint;
  if (!point) return null;

  if (point.insufficient) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: '#999' }}>
        {point.axisLabel}: Insufficient data
      </div>
    );
  }

  const trendText = point.trend === 'up' ? ' (improving)' : point.trend === 'down' ? ' (declining)' : point.trend === 'flat' ? ' (stable)' : '';

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: '#333' }}>
      <strong>{point.axisLabel}:</strong> {point.value}{trendText}
    </div>
  );
};

interface WeaknessRadarProps {
  targetUserId?: number | null;
}

const WeaknessRadar: React.FC<WeaknessRadarProps> = ({ targetUserId }) => {
  const [data, setData] = useState<WeaknessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ChartRange>('all');

  const fetchScores = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/ai-coach/weakness-scores?${params}`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      console.error('Failed to fetch weakness scores:', e);
    } finally {
      setLoading(false);
    }
  }, [range, targetUserId]);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  if (loading) {
    return (
      <div className="analytics-card">
        <h3>Weakness Analysis</h3>
        <div className="analytics-range-buttons">
          {rangeOptions.map((opt) => (
            <button
              key={opt.value}
              className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
              onClick={() => setRange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!data || !data.has_data) {
    return (
      <div className="analytics-card">
        <h3>Weakness Analysis</h3>
        <div className="analytics-range-buttons">
          {rangeOptions.map((opt) => (
            <button
              key={opt.value}
              className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
              onClick={() => setRange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13, textAlign: 'center', padding: '0 24px' }}>
          Need more ride data for weakness analysis
        </div>
      </div>
    );
  }

  const radarData: RadarDataPoint[] = AXES.map(({ key, label }) => {
    const entry = data.scores[key];
    const hasScore = entry && entry.score !== null;
    return {
      axis: key,
      axisLabel: label,
      value: hasScore ? entry.score! : 0,
      trend: hasScore ? entry.trend : null,
      insufficient: !hasScore,
    };
  });

  return (
    <div className="analytics-card">
      <h3>Weakness Analysis</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
          <PolarGrid stroke="#e5e5e5" />
          {/* @ts-ignore - Recharts 3.x type compatibility */}
          <PolarAngleAxis
            dataKey="axis"
            tick={<CustomTick data={radarData} />}
          />
          <PolarRadiusAxis
            domain={[0, 100]}
            tick={false}
            axisLine={false}
          />
          <Radar
            dataKey="value"
            stroke="#2563eb"
            fill="#3b82f6"
            fillOpacity={0.25}
            name="Score"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            formatter={legendTooltipFormatter(radarLegendDefs)}
          />
        </RadarChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, fontSize: 11, color: '#999', marginTop: 4, flexWrap: 'wrap' }}>
        <span>0-30: <strong style={{ color: '#999' }}>Weak</strong></span>
        <span>31-60: <strong style={{ color: '#999' }}>Developing</strong></span>
        <span>61-80: <strong style={{ color: '#666' }}>Good</strong></span>
        <span>81-100: <strong style={{ color: '#333' }}>Strong</strong></span>
      </div>
    </div>
  );
};

export default WeaknessRadar;
